import { useTranslation } from "react-i18next";
import PhoneInput from "./PhoneInput";
import { TbPlus, TbX, TbStar, TbStarFilled } from "react-icons/tb";
import "./multiPhoneInput.scss";

interface Props {
    value: string[];
    onChange: (v: string[]) => void;
    disabled?: boolean;
    /** Lets the doctor promote a number to index 0 (the "primary" one) — every consumer of
     *  Recipient.tel already just reads tel[0] (SMS default, OTP verification, popup displays),
     *  so reordering here is sufficient; no separate primary-phone field needed. */
    showPrimaryPicker?: boolean;
}

export default function MultiPhoneInput({ value, onChange, disabled, showPrimaryPicker }: Props) {
    const { t } = useTranslation();
    const phones = value.length > 0 ? value : [""];

    const update = (index: number, v: string) => {
        const next = [...phones];
        next[index] = v;
        onChange(next.filter(Boolean));
    };

    const remove = (index: number) => {
        const next = phones.filter((_, i) => i !== index);
        onChange(next.filter(Boolean));
    };

    const add = () => {
        onChange([...phones.filter(Boolean), ""]);
    };

    const makePrimary = (index: number) => {
        if (index === 0) return;
        const next = [...phones];
        const [chosen] = next.splice(index, 1);
        next.unshift(chosen);
        onChange(next.filter(Boolean));
    };

    return (
        <div className="multi-phone-input">
            {phones.map((phone, i) => (
                <div key={i} className="multi-phone-input__row">
                    {showPrimaryPicker && phones.filter(Boolean).length > 1 && (
                        i === 0 ? (
                            <span className="multi-phone-input__primary" title={t("form.primaryPhone")}>
                                <TbStarFilled />
                            </span>
                        ) : (
                            !disabled && (
                                <button
                                    type="button"
                                    className="multi-phone-input__make-primary"
                                    onClick={() => makePrimary(i)}
                                    title={t("form.makePrimaryPhone")}
                                    aria-label={t("form.makePrimaryPhone")}
                                >
                                    <TbStar />
                                </button>
                            )
                        )
                    )}
                    <PhoneInput
                        value={phone}
                        onChange={(v) => update(i, v)}
                        disabled={disabled}
                    />
                    {phones.length > 1 && !disabled && (
                        <button
                            type="button"
                            className="multi-phone-input__remove"
                            onClick={() => remove(i)}
                            aria-label={t("form.remove")}
                        >
                            <TbX />
                        </button>
                    )}
                </div>
            ))}
            {!disabled && <button
                type="button"
                className="multi-phone-input__add"
                onClick={add}
            >
                <TbPlus /> {t("form.addPhone")}
            </button>}
        </div>
    );
}
